import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchSharePointConnection,
  createSharePointConnection,
  updateSharePointConnection,
  deleteSharePointConnection,
  clearSharePointErrors,
} from '../../../slice/sharepointSlice'
import { useThemeColor } from '../../../hooks/useThemeColor'
import { useTranslation } from '../../../i18n/LanguageContext'
import { formatSharePointError } from '../../../utils/sharepointErrorMessage'
import { X, Trash2, Loader2 } from 'lucide-react'

const SharePointConnectionModal = ({ isOpen, onClose, organizationId, organizationName }) => {
  const dispatch = useDispatch()
  const { aispaceColor } = useThemeColor()
  const { t } = useTranslation()
  const {
    connection,
    connectionLoading,
    connectionError,
  } = useSelector((state) => state.sharepoint)

  const [form, setForm] = useState({
    tenant_id: '',
    client_id: '',
    client_secret: '',
  })
  // Snapshot of the loaded connection's tenant_id/client_id, used to detect
  // whether the admin actually changed anything before re-enabling Save —
  // editing an already-configured connection should not let Save be clicked
  // with the exact same values still in the fields.
  const [originalForm, setOriginalForm] = useState({ tenant_id: '', client_id: '' })
  const [isEditing, setIsEditing] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const [saving, setSaving] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  useEffect(() => {
    if (isOpen && organizationId) {
      setInitialLoadDone(false)
      setSaving(false)
      dispatch(clearSharePointErrors())
      dispatch(fetchSharePointConnection(organizationId)).then(() => {
        setInitialLoadDone(true)
      })
    }
  }, [isOpen, organizationId, dispatch])

  useEffect(() => {
    if (initialLoadDone) {
      if (connection) {
        setForm({
          tenant_id: connection.tenant_id || '',
          client_id: connection.client_id || '',
          client_secret: '',
        })
        setOriginalForm({
          tenant_id: connection.tenant_id || '',
          client_id: connection.client_id || '',
        })
        setIsEditing(true)
      } else {
        setForm({
          tenant_id: '',
          client_id: '',
          client_secret: '',
        })
        setOriginalForm({ tenant_id: '', client_id: '' })
        setIsEditing(false)
      }
    }
  }, [connection, initialLoadDone])

  if (!isOpen) return null

  // Loading state
  if (!initialLoadDone || connectionLoading) {
    return (
      <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-[560px] flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
          <span className="text-[14px] text-[#6B7280] ml-3">{t('manageOrganization.spConnLoading')}</span>
        </div>
      </div>
    )
  }

  // In edit mode, nothing has actually changed unless a field now differs
  // from what was loaded — client_secret starts blank, so typing anything
  // into it counts as a change.
  const hasChanges = !isEditing || (
    form.tenant_id !== originalForm.tenant_id ||
    form.client_id !== originalForm.client_id ||
    form.client_secret !== ''
  )

  const handleSave = async () => {
    if (!form.tenant_id || !form.client_id) return
    if (!isEditing && !form.client_secret) return
    if (isEditing && !hasChanges) return
    if (saving) return

    setSaving(true)
    try {
      if (isEditing) {
        const updateData = { ...form }
        if (!updateData.client_secret) delete updateData.client_secret
        await dispatch(updateSharePointConnection({
          orgId: organizationId,
          data: updateData,
        })).unwrap()
      } else {
        await dispatch(createSharePointConnection({
          ...form,
          organization_id: organizationId,
        })).unwrap()
      }
      dispatch(fetchSharePointConnection(organizationId))
      onClose()
    } catch (err) {
      // Error handled by Redux state
    } finally {
      setSaving(false)
    }
  }

  const handleDisconnect = async () => {
    if (disconnecting) return
    setDisconnecting(true)
    try {
      await dispatch(deleteSharePointConnection(organizationId)).unwrap()
      setShowDisconnectConfirm(false)
      onClose()
    } catch (err) {
      // Error handled by Redux state
    } finally {
      setDisconnecting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[560px] shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="text-[18px] font-semibold text-[#111827]">
            {isEditing ? t('manageOrganization.spConnEditTitle') : t('manageOrganization.spConnCreateTitle')}
          </h2>
          <button onClick={onClose} disabled={saving || disconnecting} className="p-1 hover:bg-[#F3F4F6] rounded-lg transition-colors">
            <X size={20} className="text-[#6B7280]" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {connectionError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-[13px] text-red-700">
              {formatSharePointError(connectionError, t)}
            </div>
          )}

          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.spConnTenantIdLabel')} *</label>
            <input
              type="text"
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={form.tenant_id}
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
              placeholder={t('manageOrganization.spConnTenantIdPlaceholder')}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">{t('manageOrganization.spConnClientIdLabel')} *</label>
            <input
              type="text"
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder={t('manageOrganization.spConnClientIdPlaceholder')}
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-[14px] font-semibold text-[#111827] mb-2">
              {t('manageOrganization.spConnClientSecretLabel')} {isEditing ? '' : '*'}
            </label>
            <input
              type="password"
              className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[14px] text-[#374151] focus:outline-none focus:ring-0 placeholder-[#9CA3AF]"
              onFocus={(e) => e.target.style.borderColor = aispaceColor}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
              value={form.client_secret}
              onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder={isEditing ? t('manageOrganization.spConnClientSecretPlaceholderEdit') : t('manageOrganization.spConnClientSecretPlaceholderCreate')}
              disabled={saving}
            />
            <p className="text-[12px] text-[#9CA3AF] mt-2">
              {t('manageOrganization.spConnSecurityNote')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB]">
          {/* Disconnect button - only when editing */}
          <div>
            {isEditing && (
              <button
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-[#DC2626] bg-[#FEE2E2] border border-[#FECACA] rounded-lg hover:bg-[#FECACA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowDisconnectConfirm(true)}
                disabled={saving || disconnecting}
              >
                <Trash2 size={16} />
                {t('manageOrganization.spConnDisconnectButton')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
              onClick={onClose}
              disabled={saving || disconnecting}
            >
              {t('common.cancel')}
            </button>
            <button
              className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
              style={{ backgroundColor: aispaceColor }}
              onClick={handleSave}
              disabled={saving || !form.tenant_id || !form.client_id || (!isEditing && !form.client_secret) || (isEditing && !hasChanges)}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('manageOrganization.spConnSavingButton')}
                </>
              ) : (
                t('manageOrganization.spConnSaveButton')
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Disconnect confirmation */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" onClick={() => !disconnecting && setShowDisconnectConfirm(false)}>
          <div className="bg-white rounded-xl w-[440px] shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[17px] font-bold text-[#111827] mb-2">{t('manageOrganization.spConnDisconnectConfirmTitle')}</h3>
            <p className="text-[14px] text-[#6B7280] mb-6">
              {t('manageOrganization.spConnDisconnectConfirmMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-5 py-2.5 text-[14px] font-medium text-[#374151] bg-white border border-[#D1D5DB] rounded-lg hover:bg-[#F9FAFB] transition-colors disabled:opacity-50"
                onClick={() => setShowDisconnectConfirm(false)}
                disabled={disconnecting}
              >
                {t('common.cancel')}
              </button>
              <button
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-[14px] font-medium text-white bg-[#DC2626] rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 min-w-[150px]"
                onClick={handleDisconnect}
                disabled={disconnecting}
              >
                {disconnecting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('manageOrganization.spConnDisconnectingButton')}
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    {t('manageOrganization.spConnDisconnectConfirmButton')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SharePointConnectionModal
